import { api } from "@/common/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CostCategory } from "../utils/categories";

// ─── Types ────────────────────────────────────────────────────────────────
// Espejo del backend (src/features/cost-allocations/{routes,service}.ts).

export type CostAllocationRow = {
  id: string;
  accountId: string;
  sourceEventId: string;
  animalId: string;
  amount: string; // numeric(14,2) → string
  category: CostCategory;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string;
  isInvalidated: boolean;
  createdAt: string;
  lotId: string | null;
  occurredAt: string;
};

export type CostAllocationsListResponse = {
  costAllocations: CostAllocationRow[];
};

// B73 (AI-D25): datos de la factura capturada (B67). `invoice` null = el
// costo no trae ningún campo de factura → la sección no se renderiza.
export type CostInvoiceDetail = {
  invoiceNumber: string | null;
  docType: string | null;
  invoiceDate: string | null;
  supplierId: string | null;
  supplierName: string | null;
  ivaAmount: string | null;
  ivaRate: string | null;
};

export type CostAllocationDetail = {
  cost: {
    id: string;
    lotId: string | null;
    category: CostCategory;
    amount: string;
    currencyCode: string;
    periodStart: string;
    periodEnd: string;
    description: string;
    paymentMethod: "cash" | "credit";
    occurredAt: string;
    recordedBy: string;
    createdAt: string;
    invoice: CostInvoiceDetail | null;
  };
  allocations: Array<{
    id: string;
    animalId: string;
    animalTag: string;
    animalName: string | null;
    amount: string;
    periodStart: string;
    periodEnd: string;
  }>;
};

export type InvoiceDocType = "factura_electronica" | "tiquete" | "recibo" | "otro";
export type IvaRate = "1" | "2" | "4" | "8" | "13" | "exento";

export type RegisterCostInput = {
  lotId: string;
  category: CostCategory;
  amount: string; // canonical decimal string
  currencyCode?: "CRC";
  periodStart: string;
  periodEnd: string;
  description: string;
  paymentMethod: "cash" | "credit";
  occurredAt: string;
  // B67 (facturas-v1 §3.1): datos de la factura recibida — opcionales,
  // snake_case espejo del payload del backend. Solo se envían si el usuario
  // capturó algo en la sección de factura.
  invoice_number?: string;
  supplier_name?: string;
  supplier_id?: string;
  invoice_date?: string; // YYYY-MM-DD, fecha de EMISIÓN (≠ occurredAt)
  doc_type?: InvoiceDocType;
  iva_amount?: string;
  iva_rate?: IvaRate;
};

export type ListCostAllocationsFilters = {
  lotId?: string;
  periodStart?: string;
  periodEnd?: string;
};

// ─── Query keys ───────────────────────────────────────────────────────────

export function costAllocationsQueryKey(accountId: string, filters?: ListCostAllocationsFilters) {
  return ["costs", accountId, filters ?? {}] as const;
}

export function costAllocationDetailKey(accountId: string, costId: string) {
  return ["costs", accountId, "detail", costId] as const;
}

// ─── Hooks ────────────────────────────────────────────────────────────────

export function useCostAllocationsList(accountId: string, filters: ListCostAllocationsFilters) {
  return useQuery({
    queryKey: costAllocationsQueryKey(accountId, filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.lotId) params.set("lotId", filters.lotId);
      if (filters.periodStart) params.set("periodStart", filters.periodStart);
      if (filters.periodEnd) params.set("periodEnd", filters.periodEnd);
      const qs = params.toString();
      return api.get<CostAllocationsListResponse>(
        `/accounts/${accountId}/cost-allocations${qs ? `?${qs}` : ""}`,
      );
    },
    enabled: Boolean(accountId),
  });
}

export function useCostAllocation(accountId: string, costId: string | undefined) {
  return useQuery({
    queryKey: costAllocationDetailKey(accountId, costId ?? ""),
    queryFn: () =>
      api.get<CostAllocationDetail>(`/accounts/${accountId}/cost-allocations/${costId}`),
    enabled: Boolean(accountId) && Boolean(costId),
  });
}

export function useRegisterCost(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Detect-by-code, not status: backend returns 422 UNUSUAL_AMOUNT for the
    // sanity-check trip (a validation error the user can resolve), not 409.
    //
    // `idempotencyKey` (UUID v4) viaja en el header `Idempotency-Key`. El
    // dialog genera un UUID por mount y lo reusa para todos los retries del
    // mismo intent (incluyendo el confirm-unusual-amount), de modo que un
    // doble-click o un retry de TanStack Query no duplique el cost. Ver
    // src/common/idempotency.ts (B23 backend).
    mutationFn: (
      input: RegisterCostInput & { confirmUnusual?: boolean; idempotencyKey?: string },
    ) => {
      const { confirmUnusual, idempotencyKey, ...payload } = input;
      const headers: Record<string, string> = {};
      if (confirmUnusual) headers["X-Confirm-Unusual-Amount"] = "true";
      if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
      return api.post<unknown>(
        `/accounts/${accountId}/cost-allocations`,
        payload,
        Object.keys(headers).length > 0 ? { headers } : undefined,
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["costs", accountId] });
      // accumulated_cost del animal cambia tras prorrateo.
      void qc.invalidateQueries({ queryKey: ["animals", accountId] });
    },
  });
}
